import React, { useEffect, useRef } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Box, Chip, Tooltip, Typography } from '@material-ui/core';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useLoadMoreNearEnd } from 'components/Table/useLoadMoreNearEnd';
import { useIsMobile } from 'components/Page/useIsMobile';
import { List, ListRowProps } from 'react-virtualized';
import { IGameFeed } from '@pvplevelup/core/dist/queries/game/gameFeed';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { GameModes, IGame } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { MapInformation } from '@pvplevelup/core/dist/interfaces/maps/IMapInformation';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';
import { MapThumbnailIcons } from 'utilities/map_icons';
import { classImages } from 'utilities/ClassImages';
import { VirtualizedList } from 'components/Table/VirtualizedList';
import { getRatingComponent } from 'views/PvP/game/GameScore';

const TEAM_COLORS = ['rgb(62, 124, 255)', 'rgb(228, 54, 55)'];
const RESULT_COLORS = {win: '#43a047', loss: '#e53935', tie: '#757575'};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'grid',
    gridTemplateColumns: '120px minmax(150px, 1fr) 110px 150px 120px minmax(280px, 1.4fr)',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`,
    padding: theme.spacing(1.25, 2, 1.25, 1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderLeftWidth: 4,
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': {
      background: 'rgba(36, 38, 44, 0.95)',
    },
  },
  map: {
    position: 'relative',
    width: 120,
    height: 68,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  mapName: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '2px 6px',
    fontSize: '0.7rem',
    fontWeight: 600,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mode: {
    fontSize: '1rem',
    fontWeight: 700,
  },
  meta: {
    fontSize: '0.8rem',
    opacity: 0.65,
  },
  center: {
    textAlign: 'center',
  },
  big: {
    fontSize: '1.3rem',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  label: {
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.55,
  },
  teams: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${theme.spacing(1)}px`,
  },
  team: {
    padding: theme.spacing(0.5, 0.75),
    borderRadius: 6,
  },
  player: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: '0.8rem',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& img': {
      width: 18,
      height: 18,
      flexShrink: 0,
    },
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    opacity: 0.7,
  },
  // phone layout: one compact card per game
  mCard: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderLeftWidth: 4,
    cursor: 'pointer',
  },
  mTop: {
    display: 'grid',
    gridTemplateColumns: '72px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: `${theme.spacing(1.25)}px`,
  },
  mMap: {
    width: 72,
    height: 44,
    borderRadius: 6,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: `${theme.spacing(1)}px`,
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderTop: '1px solid rgba(255,255,255,0.06)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    textAlign: 'center',
  },
  mValue: {
    fontSize: '1.05rem',
    fontWeight: 800,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
  mTeams: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: `${theme.spacing(1)}px`,
    marginTop: theme.spacing(0.75),
  },
  mIcons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 3,
    '& img': {
      width: 20,
      height: 20,
      borderRadius: '50%',
    },
  },
}));

const modePath = (mode?: string) => mode == GameModes.TeamDeathMatch ? 'tdm' : mode == GameModes.KingoftheHill ? 'koh'
  : mode == GameModes.Conquest ? 'conquest' : mode == GameModes.CaptureTheFlag ? 'capturetheflag' : '';

interface RowProps {
  props: ListRowProps;
  game: IGame;
  measure: () => void;
  /** character pages show the character's MMR, user pages the account's */
  byUser: boolean;
}

/** One game from the profile owner's point of view: their result, MMR change and K/D/A. */
const GameRow: React.FC<RowProps> = ({props, game, measure, byUser}) => {
  const classes = useStyles();
  const history = useHistory();
  useEffect(() => { measure(); }, []);

  const image = game.zone && game.map ? MapThumbnailIcons[game.zone || ""]?.[game.map || ""] : null;
  const participant: any = game?.performance;
  const tie = game.isTie || game.winner == null;
  const result: 'win' | 'loss' | 'tie' = tie ? 'tie' : participant && participant.team == game.winner ? 'win' : 'loss';
  const resultColor = RESULT_COLORS[result];
  const mmr: number | undefined = participant ? (byUser ? participant.ummr : participant.mmr) : undefined;
  const mmrChange: number = participant ? ((byUser ? participant.ummrChange : participant.mmrChange) || 0) : 0;
  const kda = participant ? (((participant.kills || 0) + (participant.assists || 0)) / ((participant.deaths || 0) + 1)).toFixed(2) : null;
  const team1MMR = game.teamMMR && game.teamMMR[0] != null && game.teamCMMR && game.teamCMMR[0] != null ? ((game.teamMMR[0] + game.teamCMMR[0]) / 2) : 0;
  const team2MMR = game.teamMMR && game.teamMMR[1] != null && game.teamCMMR && game.teamCMMR[1] != null ? ((game.teamMMR[1] + game.teamCMMR[1]) / 2) : 0;

  const team = (num: number) => (
    <div className={classes.team} style={{background: participant?.team == num ? 'rgba(255,255,255,0.05)' : 'transparent', boxShadow: `inset 2px 0 0 ${TEAM_COLORS[num]}`}}>
      {game.players?.filter((p) => p.team === num).map((player, i) => {
        const me = player.name == participant?.name;
        return (
          <div key={i} className={classes.player}>
            <img src={classImages.getColorIcon(player.class as AocClasses)} title={player.class} />
            <span style={{fontWeight: me ? 700 : 400, opacity: me ? 1 : 0.75}}>{player.name}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div key={game._id} style={{paddingBottom: 1}}>
      <div className={classes.card} style={{borderLeftColor: resultColor}} onClick={() => history.push(`/game/${modePath(game.mode)}/${game._id}`)}>
        <div className={classes.map} style={{backgroundImage: image ? `url(${image})` : undefined}}>
          {game.map && MapInformation[game.map] && <div className={classes.mapName}>{MapInformation[game.map].name}</div>}
        </div>
        <div style={{minWidth: 0}}>
          <div className={classes.mode}>{game.mode}</div>
          <Box display="flex" alignItems="center" style={{gap: 6, margin: '4px 0'}}>
            <Chip size="small" label={result == 'win' ? 'Win' : result == 'loss' ? 'Loss' : 'Tie'} style={{background: resultColor, color: '#fff', fontWeight: 600}} />
          </Box>
          <div className={classes.meta}>{moment(game.createdAt).format("MMM D, YYYY h:mm a")} · {hhmmss((game.totalTimeElapsed || 0), false)}</div>
        </div>
        <div className={classes.center}>
          <div className={classes.big}>
            <span style={{color: participant?.team == 0 ? resultColor : undefined}}>{game.teams?.[0]?.score}</span>
            <span style={{opacity: 0.5}}> : </span>
            <span style={{color: participant?.team == 1 ? resultColor : undefined}}>{game.teams?.[1]?.score}</span>
          </div>
          <div className={classes.meta}>{game.teams?.[0]?.kills ?? '-'} : {game.teams?.[1]?.kills ?? '-'} kills</div>
          <Tooltip title="Average MMR difference between the teams">
            <div className={classes.meta}>
              MMR diff <span style={{fontWeight: 700, color: team1MMR > team2MMR ? TEAM_COLORS[0] : team2MMR > team1MMR ? TEAM_COLORS[1] : undefined}}>{Math.round(Math.abs(team1MMR - team2MMR))}</span>
            </div>
          </Tooltip>
        </div>
        <div className={classes.center}>
          {participant?.isPlacement
            ? <div className={classes.big} style={{fontSize: '1rem'}}>Placement</div>
            : <Tooltip title={mmr != null ? `${mmr} before the match` : ''}>
                <div>
                  <span className={classes.big}>{mmr != null ? mmr + mmrChange : '-'}</span>
                  {mmrChange != 0 &&
                    <span style={{marginLeft: 6, fontWeight: 700, color: mmrChange > 0 ? RESULT_COLORS.win : RESULT_COLORS.loss}}>
                      {mmrChange > 0 ? '+' : ''}{mmrChange}
                    </span>
                  }
                </div>
              </Tooltip>
          }
          <div className={classes.label}>MMR</div>
          {participant &&
            <Box display="flex" justifyContent="center" style={{gap: 12, marginTop: 4}}>
              <Tooltip title="Performance this game"><span>{getRatingComponent(participant.rating || 0)}</span></Tooltip>
              {!participant.isPlacement && participant.player_rating != null &&
                <Tooltip title="Average performance over the last 20 games"><span className={classes.meta}>avg {participant.player_rating}</span></Tooltip>
              }
            </Box>
          }
        </div>
        <div className={classes.center}>
          {participant ?
            <React.Fragment>
              <div className={classes.big} style={{fontSize: '1.1rem'}}>
                {participant.kills}
                <span style={{opacity: 0.4}}> / </span>
                <span style={{color: (participant.deaths || 0) > (participant.kills || 0) ? '#ff8a50' : undefined}}>{participant.deaths}</span>
                <span style={{opacity: 0.4}}> / </span>
                {participant.assists}
              </div>
              <div className={classes.meta}><b>{kda}</b> KDA</div>
              <Tooltip title={`${participant.eKills || 0} effective kills, ${participant.fBlows || 0} finishing blows`}>
                <div className={classes.meta}>{participant.eKills || 0} EK · {participant.fBlows || 0} FB</div>
              </Tooltip>
            </React.Fragment>
          : <span className={classes.meta}>-</span>}
        </div>
        <div className={classes.teams}>
          {team(0)}
          {team(1)}
        </div>
      </div>
    </div>
  );
}

/** The phone version of a game row: result and map on top, the headline numbers, then both teams' classes. */
const MobileGameRow: React.FC<RowProps> = ({game, measure, byUser}) => {
  const classes = useStyles();
  const history = useHistory();
  useEffect(() => { measure(); }, []);

  const image = game.zone && game.map ? MapThumbnailIcons[game.zone || ""]?.[game.map || ""] : null;
  const participant: any = game?.performance;
  const tie = game.isTie || game.winner == null;
  const result: 'win' | 'loss' | 'tie' = tie ? 'tie' : participant && participant.team == game.winner ? 'win' : 'loss';
  const resultColor = RESULT_COLORS[result];
  const mmr: number | undefined = participant ? (byUser ? participant.ummr : participant.mmr) : undefined;
  const mmrChange: number = participant ? ((byUser ? participant.ummrChange : participant.mmrChange) || 0) : 0;

  const icons = (num: number) => (
    <div className={classes.mIcons} style={{justifyContent: num == 0 ? 'flex-end' : 'flex-start'}}>
      {game.players?.filter((p) => p.team === num).map((player, i) => (
        <img key={i} src={classImages.getColorIcon(player.class as AocClasses)} title={player.name}
          style={{boxShadow: player.name == participant?.name ? `0 0 0 2px ${TEAM_COLORS[num]}` : undefined}} />
      ))}
    </div>
  );

  return (
    <div key={game._id} style={{paddingBottom: 1}}>
      <div className={classes.mCard} style={{borderLeftColor: resultColor}} onClick={() => history.push(`/game/${modePath(game.mode)}/${game._id}`)}>
        <div className={classes.mTop}>
          <div className={classes.mMap} style={{backgroundImage: image ? `url(${image})` : undefined}} />
          <div style={{minWidth: 0}}>
            <div className={`${classes.mode} ${classes.ellipsis}`} style={{fontSize: '0.95rem'}}>{game.mode}</div>
            <div className={`${classes.meta} ${classes.ellipsis}`}>{game.map && MapInformation[game.map] ? MapInformation[game.map].name : ''}</div>
            <div className={`${classes.meta} ${classes.ellipsis}`}>{moment(game.createdAt).format("MMM D, h:mm a")} · {hhmmss((game.totalTimeElapsed || 0), false)}</div>
          </div>
          <Chip size="small" label={result == 'win' ? 'Win' : result == 'loss' ? 'Loss' : 'Tie'} style={{background: resultColor, color: '#fff', fontWeight: 600}} />
        </div>
        <div className={classes.mStats}>
          <div>
            <div className={classes.mValue}>
              <span style={{color: participant?.team == 0 ? resultColor : undefined}}>{game.teams?.[0]?.score}</span>
              <span style={{opacity: 0.5}}> : </span>
              <span style={{color: participant?.team == 1 ? resultColor : undefined}}>{game.teams?.[1]?.score}</span>
            </div>
            <div className={classes.label}>Score</div>
          </div>
          <div>
            <div className={classes.mValue}>
              {participant?.isPlacement ? <span style={{fontSize: '0.85rem'}}>Placement</span> :
                <React.Fragment>
                  {mmr != null ? mmr + mmrChange : '-'}
                  {mmrChange != 0 &&
                    <span style={{marginLeft: 4, fontSize: '0.8rem', color: mmrChange > 0 ? RESULT_COLORS.win : RESULT_COLORS.loss}}>
                      {mmrChange > 0 ? '+' : ''}{mmrChange}
                    </span>
                  }
                </React.Fragment>
              }
            </div>
            <div className={classes.label}>MMR</div>
          </div>
          <div>
            <div className={classes.mValue}>
              {participant ?
                <React.Fragment>
                  {participant.kills}<span style={{opacity: 0.4}}>/</span>
                  <span style={{color: (participant.deaths || 0) > (participant.kills || 0) ? '#ff8a50' : undefined}}>{participant.deaths}</span>
                  <span style={{opacity: 0.4}}>/</span>{participant.assists}
                </React.Fragment>
              : '-'}
            </div>
            <div className={classes.label}>K / D / A</div>
          </div>
        </div>
        <div className={classes.mTeams}>
          {icons(0)}
          <span className={classes.label}>vs</span>
          {icons(1)}
        </div>
      </div>
    </div>
  );
}

interface Props {
  gameFeed: IGameFeed | undefined;
  fetchMoreRecords: () => void;
  /** user pages show the account's MMR */
  byUser?: boolean;
  /** height of the scrolling list; fills the tab by default */
  height?: string;
}

/** The Game History tab of the character and user pages, in the same style as Past Games. */
export const ProfileGames: React.FC<Props> = ({gameFeed, fetchMoreRecords, byUser = false, height = '100%'}) => {
  const classes = useStyles();
  const listRef = useRef<List>(null);
  const mobile = useIsMobile();
  const games = gameFeed?.games || [];
  const onRowsRendered = useLoadMoreNearEnd(games.length, gameFeed?.hasNext, fetchMoreRecords, games[0]?._id);
  return (
    <div style={{height, position: 'relative'}}>
      <VirtualizedList
        // remounted when switching layouts so the rows are measured again
        key={mobile ? 'mobile' : 'desktop'}
        reference={listRef}
        data={games}
        overscanRowCount={10}
        // a row's columns, gaps, padding and border need ~1043px, plus room for the list's own scrollbar
        minWidth={mobile ? undefined : 1064}
        onRowsRendered={onRowsRendered}
        customRenderer={(props: ListRowProps, game: IGame, measure: () => void) => mobile
          ? <MobileGameRow props={props} game={game} measure={measure} byUser={byUser} />
          : <GameRow props={props} game={game} measure={measure} byUser={byUser} />
        }
      />
      {gameFeed && games.length == 0 &&
        <div className={classes.empty} style={{position: 'absolute', top: 0, left: 0, right: 0}}>
          <Typography>No games with these filters.</Typography>
        </div>
      }
    </div>
  );
}
